import moment from "moment"

/**
 * Formats a date for display in the user's local timezone
 * @param date - The date to format in local time (Date object, ISO string, or timestamp)
 * @param withTime - Whether to include time in the output (default: false)
 * @param is12HourClock - Whether to use 12-hour format (default: false, uses 24-hour)
 * @param withSeconds - Whether to include seconds in the time (default: false)
 * @param useEndOfDay - Whether to set time to end of day (23:59:59) (default: false)
 * @param withUTCTimezone - Whether to interpret the date as UTC. Adds "Z" suffix if missing. (default: false)
 * @returns Formatted date string in MM/DD/YYYY format in local time, optionally with time
 * @example
 * // Date only
 * formatDate(new Date()) // "01/05/2024"
 * // Date with time in 24-hour format
 * formatDate(new Date(), true) // "01/05/2024 14:30"
 * // Date with time in 12-hour format
 * formatDate(new Date(), true, true) // "01/05/2024 02:30 PM"
 * // Date at end of day
 * formatDate(new Date(), false, false, false, true) // "01/05/2024"
 * // Date with time at end of day
 * formatDate(new Date(), true, false, false, true) // "01/05/2024 23:59:59"
 */
export const formatDate = (
  date: Date | string,
  withTime = false,
  is12HourClock = false,
  withSeconds = false,
  useEndOfDay = false,
  withUTCTimezone = false
): string => {
  if (!date) {
    return ""
  }

  // set 12 or 24 hours format
  const timeFormat = is12HourClock
    ? `hh:mm${withSeconds ? ":ss A" : " A"}`
    : `HH:mm${withSeconds ? ":ss" : ""}`
  const dateFormat = `MM/DD/YYYY${withTime ? ` ${timeFormat}` : ""}`

  // Create a moment in local timezone
  let localMoment
  if (withUTCTimezone) {
    const dateWithUTCTimezone = String(date).includes("Z") ? date : date + "Z"
    localMoment = moment(dateWithUTCTimezone)
  } else {
    localMoment = moment(date)
  }

  // If useEndOfDay is true, set to end of day
  if (useEndOfDay) {
    localMoment = localMoment.endOf("day")
  }

  return localMoment.format(dateFormat)
}

/**
 * Formats a date for display in UTC timezone
 * @param date - The date to format in local time (Date object, ISO string, or timestamp)
 * @param withTime - Whether to include time in the output (default: false)
 * @param is12HourClock - Whether to use 12-hour format (default: false, uses 24-hour)
 * @param withSeconds - Whether to include seconds in the time (default: false)
 * @param useEndOfDay - Whether to set time to end of day (23:59:59) (default: false)
 * @returns Formatted date string in MM/DD/YYYY format converted to UTC, optionally with time
 * @example
 * // Date only in UTC (local date: 01/05/2024)
 * formatUTCDate(new Date('2024-01-05 14:30:00')) // "01/05/2024"
 * // Date with time converted from local (EST -5) to UTC
 * formatUTCDate(new Date('2024-01-05 14:30:00'), true) // "01/05/2024 19:30"
 * // Date with time in UTC, 12-hour format
 * formatUTCDate(new Date('2024-01-05 14:30:00'), true, true) // "01/05/2024 07:30 PM"
 * // Date at end of day in UTC
 * formatUTCDate(new Date('2024-01-05'), false, false, false, true) // "01/05/2024"
 * // Date with time at end of day in UTC (23:59:59 local becomes 04:59:59 UTC for EST)
 * formatUTCDate(new Date('2024-01-05'), true, false, false, true) // "01/06/2024 04:59:59"
 */
export const formatUTCDate = (
  date: Date | string,
  withTime = false,
  is12HourClock = false,
  withSeconds = false,
  useEndOfDay = false
): string => {
  if (!date) {
    return ""
  }

  // set 12 or 24 hours format
  const timeFormat = is12HourClock
    ? `hh:mm${withSeconds ? ":ss A" : " A"}`
    : `HH:mm${withSeconds ? ":ss" : ""}`
  const dateFormat = `MM/DD/YYYY${withTime ? ` ${timeFormat}` : ""}`

  // Create a moment in local timezone
  const localMoment = moment(date)

  // Convert from local time to UTC before formatting
  let utcMoment = localMoment.utc()

  // If useEndOfDay is true, set to end of day
  if (useEndOfDay) {
    utcMoment = utcMoment.endOf("day")
  }

  return utcMoment.format(dateFormat)
}

/**
 * Formats a local date to UTC, optionally adjusting to start/end of day in local time.
 * @param date - Input date (Date, ISO string, timestamp, or 'YYYY-MM-DD' string).
 * @param withTime - Include time in output (default: false).
 * @param is12HourClock - Use 12-hour format (default: false).
 * @param withSeconds - Include seconds in time (default: false).
 * @param useStartOfDay - Set to start of day (00:00:00) in local time before UTC conversion (default: false).
 * @param useEndOfDay - Set to end of day (23:59:59) in local time before UTC conversion (default: false).
 * @param useISOFormat - Return date in ISO 8601 format instead of MM/DD/YYYY (default: false)
 * @returns Formatted date string in MM/DD/YYYY format (UTC), optionally with time.
 * @example
 * // Local timezone: EST (UTC-5)
 * formatLocalDateToUTCDate({ date: '2025-07-08' }) // "07/08/2025" (00:00 EST → 05:00 UTC)
 * formatLocalDateToUTCDate({ date: '2025-07-08', withTime: true }) // "07/08/2025 05:00"
 * formatLocalDateToUTCDate({ date: '2025-07-08', useEndOfDay: true, withTime: true }) // "07/09/2025 04:59" (23:59 EST → 04:59 UTC next day)
 * formatLocalDateToUTCDate({ date: '2025-07-08', useISOFormat: true }) // "2025-07-08T05:00:00Z"
 */
export const formatLocalDateToUTCDate = ({
  date,
  withTime = false,
  is12HourClock = false,
  withSeconds = false,
  useStartOfDay = false,
  useEndOfDay = false,
  useISOFormat = false
}: {
  date: Date | string
  withTime?: boolean
  is12HourClock?: boolean
  withSeconds?: boolean
  useStartOfDay?: boolean
  useEndOfDay?: boolean
  useISOFormat?: boolean
}): string => {
  if (!date) {
    return ""
  }

  let localMoment = moment(date)

  if (useStartOfDay || useEndOfDay) {
    if (useStartOfDay) {
      localMoment = localMoment.startOf("day")
    } else {
      localMoment = localMoment.endOf("day")
    }
  }

  let utcMoment = localMoment.utc()

  if (useISOFormat) {
    return withTime
      ? withSeconds
        ? utcMoment.toISOString()
        : utcMoment.format("YYYY-MM-DDTHH:mm") + "Z"
      : utcMoment.format("YYYY-MM-DD")
  }

  const timeFormat = is12HourClock
    ? `hh:mm${withSeconds ? ":ss A" : " A"}`
    : `HH:mm${withSeconds ? ":ss" : ""}`

  const dateFormat = `MM/DD/YYYY${withTime ? ` ${timeFormat}` : ""}`

  return utcMoment.format(dateFormat)
}

/**
 * Adds specified time units to a date
 * @param date - Input date (Date, string, Moment, or timestamp)
 * @param timeToAdd - Object containing time units to add
 * @returns New Date object with added time
 *
 * @example
 * addTimeToDate(new Date(), { hours: 2, minutes: 30 }) // Adds 2.5 hours to current time
 * addTimeToDate('2025-07-08', { days: 1 }) // Returns 2025-07-09 00:00:00
 */
export const addTimeToDate = ({
  date,
  timeToAdd = {}
}: {
  date: Date | string
  timeToAdd: {
    hours?: number
    minutes?: number
    seconds?: number
    days?: number
  }
}): Date => {
  const { hours = 0, minutes = 0, seconds = 0, days = 0 } = timeToAdd

  return moment(date)
    .add(days, "days")
    .add(hours, "hours")
    .add(minutes, "minutes")
    .add(seconds, "seconds")
    .toDate()
}

/**
 * Get the current user's timezone abbreviation
 * @returns Timezone abbreviation (e.g., "EST", "PST", "UTC")
 */
export const getUserTimezoneAbbreviation = (): string => {
  const date = new Date()
  const timeString = date.toLocaleTimeString("en-US", {
    timeZoneName: "short"
  })
  // Extract timezone abbreviation from the string
  const match = timeString.match(/[A-Z]{2,4}(?:\+\d+)?$/)
  return match ? match[0] : "UTC"
}

/**
 * Get the full timezone name
 * @returns Full timezone name (e.g., "Eastern Standard Time")
 */
export const getUserTimezoneName = (): string => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return timezone || "UTC"
  } catch {
    return "UTC"
  }
}

/**
 * Get timezone help text for date pickers
 * @returns Help text explaining timezone behavior
 */
export const getTimezoneHelpText = (): string => {
  const timezone = getUserTimezoneName()
  return `Times are in your local timezone (${timezone})`
}

/**
 * Format a date for display in tables (date only, no time)
 * @param date - Date to format
 * @returns Formatted date string in M/D/YYYY format
 */
export const formatTableDate = (date: Date | string | null): string => {
  if (!date) return "-"

  const dateObj = typeof date === "string" ? new Date(date) : date

  // Format: M/D/YYYY
  return dateObj.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric"
  })
}

/**
 * Format a datetime for display in tables with timezone
 * @param date - Date to format
 * @returns Formatted datetime string with timezone abbreviation (M/D/YYYY, H:MM AM/PM TZ)
 */
export const formatTableDateTime = (date: Date | string | null): string => {
  if (!date) return "-"

  const dateObj = typeof date === "string" ? new Date(date) : date
  const timezone = getUserTimezoneAbbreviation()

  // Format: M/D/YYYY, H:MM AM/PM TZ
  const dateStr = dateObj.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric"
  })

  const timeStr = dateObj.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  })

  return `${dateStr}, ${timeStr} ${timezone}`
}

/**
 * Converts a local date to UTC by treating the local date/time components as UTC
 * This is used for assignments to ensure dates are stored consistently
 * @param date - The date to convert (defaults to current date)
 * @returns Date object with local components interpreted as UTC
 * @example
 * // If local time is 2024-01-05 14:30:00 EST
 * getLocalDateAsUTC(new Date('2024-01-05 14:30:00'))
 * // Returns: 2024-01-05 14:30:00 UTC (not 2024-01-05 19:30:00 UTC)
 */
export const getLocalDateAsUTC = (date = new Date()) => {
  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
      date.getMilliseconds()
    )
  )
}
